import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Floating "Install app" prompt for Android/Chrome.
 *
 * Listens for the browser's `beforeinstallprompt` event and offers a one-tap
 * install. Dismissed state is remembered in localStorage so we don't nag.
 *
 * iOS/Safari doesn't fire this event — users add via Share → Add to Home Screen
 * (we show a hint card instead on iOS).
 */
const STORAGE_KEY = "spark_pwa_install_dismissed_at";
const NAG_AFTER_DAYS = 14; // re-prompt after dismissal

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  // eslint-disable-next-line no-undef
  navigator.standalone === true;

const wasDismissedRecently = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (!ts) return false;
  const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return ageDays < NAG_AFTER_DAYS;
};

export const InstallAppPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (wasDismissedRecently()) return;

    if (isIos()) {
      // Slight delay so it doesn't fight the first paint
      const t = setTimeout(() => setShowIosHint(true), 4000);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      // ignore — user dismissed
    }
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setDeferred(null);
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
    setShowIosHint(false);
  };

  if (show && deferred) {
    return (
      <div
        className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[360px] z-[60] bg-spark-ink text-white rounded-lg shadow-2xl border border-spark-teal/40 p-4 flex items-start gap-3"
        data-testid="pwa-install-prompt"
        role="dialog"
        aria-label="Install Spark Scooter app"
      >
        <div className="size-10 rounded-md bg-spark-teal/15 flex items-center justify-center shrink-0">
          <Download className="size-5 text-spark-yellow" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display uppercase tracking-wider text-xs font-bold text-spark-yellow">Install Spark on your device</p>
          <p className="text-sm text-slate-200 mt-1 leading-snug">
            Add the workshop app to your home screen — opens fullscreen, works on flaky signal, no browser tabs.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={install}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-spark-yellow text-spark-ink text-xs font-display uppercase tracking-wider font-bold hover:bg-yellow-400 transition"
              data-testid="pwa-install-btn"
            >
              <Download className="size-3.5" /> Install
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-1.5 text-xs uppercase tracking-wider font-semibold text-slate-300 hover:text-white transition"
              data-testid="pwa-install-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Close" className="text-slate-400 hover:text-white p-1 -mt-1 -mr-1">
          <X className="size-4" />
        </button>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div
        className="fixed bottom-4 inset-x-4 z-[60] bg-spark-ink text-white rounded-lg shadow-2xl border border-spark-teal/40 p-4 flex items-start gap-3"
        data-testid="pwa-ios-hint"
        role="dialog"
        aria-label="Add to Home Screen instructions"
      >
        <div className="size-10 rounded-md bg-spark-teal/15 flex items-center justify-center shrink-0">
          <Download className="size-5 text-spark-yellow" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display uppercase tracking-wider text-xs font-bold text-spark-yellow">Add Spark to your home screen</p>
          <p className="text-sm text-slate-200 mt-1 leading-snug">
            Tap the <strong>Share</strong> icon below, then <strong>Add to Home Screen</strong>. Opens like an app.
          </p>
          <button onClick={dismiss} className="mt-2 text-xs uppercase tracking-wider font-semibold text-spark-yellow hover:text-yellow-300">
            Got it
          </button>
        </div>
        <button onClick={dismiss} aria-label="Close" className="text-slate-400 hover:text-white p-1 -mt-1 -mr-1">
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return null;
};

export default InstallAppPrompt;
